// An axios adapter that answers every request from fixtures instead of hitting
// api-staging.4schools.io. Installed on the shared axios instance in
// store/api.ts when USE_MOCK_API is on, so all eight service classes in
// store/services are covered without touching a single call site.

import type { AxiosAdapter, AxiosRequestConfig, AxiosResponse } from "axios";
import {
	MOCK_ANNOUNCEMENTS,
	MOCK_CARD,
	MOCK_DISCOUNTS,
	MOCK_INSTITUTIONS,
	MOCK_MAIN_USER,
	MOCK_REFRESH_TOKEN,
	MOCK_TOKEN,
	MOCK_USER_INFO,
} from "./fixtures";

/** Simulated latency, so loading states are exercised rather than skipped. */
const LATENCY_MS = 180;

type Route = {
	method: string;
	/** Matched against the path with the baseURL stripped. */
	test: RegExp;
	respond: (config: AxiosRequestConfig) => { status?: number; data: any };
};

const routes: Route[] = [
	// ── Auth ────────────────────────────────────────────────────────────────
	{
		method: "post",
		test: /\/users\/login\/signin$/,
		respond: () => ({ data: MOCK_MAIN_USER }),
	},
	{
		method: "post",
		test: /\/users\/login\/refresh$/,
		respond: () => ({
			data: { token: MOCK_TOKEN, refresh_token: MOCK_REFRESH_TOKEN },
		}),
	},
	{
		method: "post",
		test: /\/users\/login\/get_email_and_name$/,
		respond: () => ({
			data: { email: MOCK_USER_INFO.email, name: MOCK_USER_INFO.name },
		}),
	},
	{
		method: "post",
		test: /\/users\/login\/(send_email|otp_login)$/,
		respond: () => ({ data: MOCK_MAIN_USER }),
	},
	{
		method: "post",
		test: /\/users\/sign_up/,
		respond: () => ({ data: MOCK_MAIN_USER }),
	},
	{
		method: "post",
		test: /\/users\/password\/(verify_email|verify_otp|reset_password)$/,
		respond: () => ({ data: { success: true } }),
	},

	// ── Profile & card ──────────────────────────────────────────────────────
	{
		method: "get",
		test: /\/users\/profile$/,
		respond: () => ({ data: MOCK_USER_INFO }),
	},
	{
		method: "get",
		test: /\/users\/profile\/verify_account_status$/,
		respond: () => ({ data: { status: "confirmed" } }),
	},
	{
		method: "get",
		test: /\/users\/profile\/get_card$/,
		respond: () => ({ data: MOCK_CARD }),
	},
	{
		method: "post",
		test: /\/users\/profile\/get_card_v2$/,
		respond: () => ({ data: MOCK_CARD }),
	},
	{
		method: "post",
		test: /\/users\/profile\/card\/images\/standard$/,
		respond: () => ({ data: { display_images: MOCK_CARD.display_images } }),
	},
	{
		method: "put",
		test: /\/users\/profile\/update\/profile_image/,
		respond: () => ({ data: { profile_image_id: MOCK_CARD.image_id } }),
	},

	// ── Content the Home screen pulls in ────────────────────────────────────
	{
		method: "get",
		test: /\/users\/discounts$/,
		respond: () => ({ data: MOCK_DISCOUNTS }),
	},
	{
		method: "get",
		test: /\/users\/discounts\/highlights$/,
		respond: () => ({ data: MOCK_DISCOUNTS.map(d => d.id) }),
	},
	{
		method: "get",
		test: /\/users\/discounts\/documents\/highlights\/?$/,
		respond: () => ({ data: [] }),
	},
	{
		method: "get",
		test: /\/users\/discounts\/documents\/?$/,
		respond: () => ({ data: [] }),
	},
	{
		method: "get",
		test: /\/users\/announcements\/?$/,
		respond: () => ({ data: MOCK_ANNOUNCEMENTS }),
	},
	{
		method: "get",
		test: /\/users\/announcements\/documents\//,
		respond: () => ({ data: [] }),
	},

	// ── Card creation flow ──────────────────────────────────────────────────
	{
		method: "get",
		test: /\/landing_page\/get_institutions$/,
		respond: () => ({ data: MOCK_INSTITUTIONS }),
	},
	{
		method: "post",
		test: /\/landing_page\/check_credentials$/,
		respond: () => ({ data: { valid: true } }),
	},
	{
		method: "post",
		test: /\/landing_page\/register_card$/,
		respond: () => ({ data: MOCK_CARD }),
	},
];

const pathOf = (config: AxiosRequestConfig): string => {
	const raw = config.url ?? "";
	// Strip the baseURL (or any absolute origin) so routes match on path alone.
	const withoutOrigin = raw.replace(/^https?:\/\/[^/]+/, "");
	return withoutOrigin.split("?")[0].replace(/\/$/, "") || "/";
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const mockAdapter: AxiosAdapter = async config => {
	const method = (config.method ?? "get").toLowerCase();
	const path = pathOf(config);

	await delay(LATENCY_MS);

	const route = routes.find(r => r.method === method && r.test.test(path));
	const result = route
		? route.respond(config)
		: // Unknown endpoints answer with an empty 200 rather than a network
		  // error, so a screen this build does not cover degrades to "no data"
		  // instead of throwing.
		  { status: 200, data: {} };

	if (!route && __DEV__) {
		console.info(`[mock api] unhandled ${method.toUpperCase()} ${path} -> {}`);
	}

	const response: AxiosResponse = {
		data: result.data,
		status: result.status ?? 200,
		statusText: "OK",
		headers: { "content-type": "application/json" },
		config,
		request: { mock: true, method, path },
	};

	return response;
};

export default mockAdapter;
